import type { Dapp, Release } from '@src/dto';
import type { ReactNode } from 'react';
import { LooseNftMetadata } from '../../../../types/api';
import {
  detectNftStorageProvider,
  getNftStorageProviderLabel,
} from '../../../../utils/nftStorageUtils';
import {
  extractFilename,
  getMetadataSolanaDappStore,
} from '../../../../utils/nftMetadataHelpers';
import { CopyableValue } from '../../../common/detailParts';
import { truncateHash } from '../../../common/truncateHash';
import { PortalCard } from '../../../portal';
import { DetailDisclosure, DetailLabel } from '../releaseDetailParts';

type NftInformationSolanaDappStore = {
  files?: Array<{
    purpose?: string;
    uri?: string;
  }>;
};

interface NftInformationSectionProps {
  release: Release;
  dapp: Dapp | null;
  nftMetadata: LooseNftMetadata | null;
}

export function NftInformationSection({
  release,
  dapp,
  nftMetadata,
}: NftInformationSectionProps) {
  if (!release.nftMintAddress && !dapp?.nftMintAddress) {
    return null;
  }

  const cluster = release.nftCluster || 'devnet';
  const providerInfo = detectNftStorageProvider(
    release.nftMetadataUri,
    release.uploadProvider,
    release.uploadFacilitator,
  );
  const metadataLabel = release.nftMetadataUri
    ? getNftStorageProviderLabel(release.nftMetadataUri, release.uploadProvider)
    : null;
  const solanaDappStore =
    getMetadataSolanaDappStore<NftInformationSolanaDappStore>(nftMetadata);
  const files = solanaDappStore?.files || [];
  const hasExtraStorageDetails =
    Boolean(release.uploadProviderId) ||
    files.length > 0 ||
    Boolean(nftMetadata?.image) ||
    Boolean(nftMetadata?.animation_url) ||
    Boolean(nftMetadata?.external_url);

  return (
    <PortalCard className="p-6">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-base font-bold text-white">On-chain</h2>
        <span
          className="shrink-0 truncate rounded-full bg-seeker-800 px-2.5 py-1 text-xs text-seeker-300"
          title={providerInfo.name}
        >
          {providerInfo.name}
        </span>
      </div>

      <dl className="mt-5 divide-y divide-seeker-800">
        {release.nftMintAddress && (
          <OnChainRow label="Mint">
            <CopyableValue
              valueClassName="text-base"
              value={release.nftMintAddress}
              display={truncateHash(release.nftMintAddress, 8, 8)}
              href={`https://explorer.solana.com/address/${release.nftMintAddress}?cluster=${cluster}`}
            />
          </OnChainRow>
        )}
        {dapp?.nftMintAddress && (
          <OnChainRow label="App Collection">
            <CopyableValue
              valueClassName="text-base"
              value={dapp.nftMintAddress}
              display={truncateHash(dapp.nftMintAddress, 8, 8)}
              href={`https://explorer.solana.com/address/${dapp.nftMintAddress}?cluster=${cluster}`}
            />
          </OnChainRow>
        )}
        {release.nftTransactionSignature && (
          <OnChainRow label="Transaction">
            <CopyableValue
              valueClassName="text-base"
              value={release.nftTransactionSignature}
              display={truncateHash(release.nftTransactionSignature, 8, 8)}
              href={`https://explorer.solana.com/tx/${release.nftTransactionSignature}?cluster=${cluster}`}
            />
          </OnChainRow>
        )}
        {release.nftMetadataUri && (
          <OnChainRow label="Metadata">
            <CopyableValue
              valueClassName="text-base"
              value={release.nftMetadataUri}
              display={`View on ${metadataLabel ?? 'storage'}`}
              href={release.nftMetadataUri}
              mono={false}
            />
          </OnChainRow>
        )}
      </dl>

      {hasExtraStorageDetails && (
        <DetailDisclosure label="Storage details">
          <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2">
            <div className="flex flex-col gap-1">
              <DetailLabel>Provider type</DetailLabel>
              <span className="text-sm text-seeker-300">
                {providerInfo.type}
              </span>
            </div>
            {release.uploadProviderId && (
              <div className="flex flex-col gap-1">
                <DetailLabel>Provider ID</DetailLabel>
                <CopyableValue
                  value={release.uploadProviderId}
                  display={truncateHash(release.uploadProviderId, 12, 12)}
                />
              </div>
            )}
            {(files.length > 0 ||
              nftMetadata?.image ||
              nftMetadata?.animation_url ||
              nftMetadata?.external_url) && (
              <div className="flex flex-col gap-2 md:col-span-2">
                <DetailLabel>NFT files</DetailLabel>
                {files.map((file, index) => (
                  <NftFileRow
                    key={`${file?.uri || 'file'}-${index}`}
                    label={file?.purpose || 'file'}
                    uri={file?.uri || ''}
                  />
                ))}
                {nftMetadata?.image && (
                  <NftFileRow label="Image" uri={nftMetadata.image} />
                )}
                {nftMetadata?.animation_url && (
                  <NftFileRow
                    label="Animation"
                    uri={nftMetadata.animation_url}
                  />
                )}
                {nftMetadata?.external_url && (
                  <NftFileRow label="External" uri={nftMetadata.external_url} />
                )}
              </div>
            )}
          </div>
        </DetailDisclosure>
      )}
    </PortalCard>
  );
}

function OnChainRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-1 items-center gap-y-1 py-3 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-x-6">
      <dt>
        <DetailLabel>{label}</DetailLabel>
      </dt>
      {/* Shrink to the value so the copy/open actions sit beside it, not at the far edge. */}
      <dd className="min-w-0">
        <div className="w-fit max-w-full">{children}</div>
      </dd>
    </div>
  );
}

function NftFileRow({ label, uri }: { label: string; uri: string }) {
  if (!uri) return null;
  return (
    <div className="flex min-w-0 items-center justify-between gap-3">
      <DetailLabel className="min-w-[90px]">{label}</DetailLabel>
      <div className="min-w-0 flex-1">
        <CopyableValue
          value={uri}
          display={extractFilename(uri)}
          href={uri}
          mono
        />
      </div>
    </div>
  );
}
